import type { DefaultOptions, QueryKey } from '@tanstack/react-query';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { logError } from '@zg-rentals/log-error';

export type QueryMeta = {
  context?: Record<string, unknown>;
  tags?: Array<string>;
};

export type MutationMeta = {
  context?: Record<string, unknown>;
  invalidates?: Array<string>;
};

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: QueryMeta;
    mutationMeta: MutationMeta;
  }
}

export type QueryErrorContext = (
  error: Error,
  queryKey: QueryKey,
  queryMeta?: QueryMeta,
) => Parameters<typeof logError>[0]['context'];

export type ShouldLogQueryError = (error: Error, queryKey: QueryKey, queryMeta?: QueryMeta) => boolean;

export type MutationErrorContext = (
  error: Error,
  mutationVariables: unknown,
  mutationMeta?: MutationMeta,
) => Parameters<typeof logError>[0]['context'];

export type CreateQueryClientOptions = {
  defaultOptions?: DefaultOptions;
  queryErrorContext?: QueryErrorContext;
  shouldLogQueryError?: ShouldLogQueryError;
  mutationErrorContext?: MutationErrorContext;
};

export const defaultQueryClientOptions = {
  queries: {
    refetchOnWindowFocus: false,
  },
} satisfies DefaultOptions;

const defaultQueryErrorContext: QueryErrorContext = (_error, queryKey, { context, tags } = {}) => ({
  queryKey,
  tags,
  ...context,
});

const defaultShouldLogQueryError: ShouldLogQueryError = () => true;

const defaultMutationErrorContext: MutationErrorContext = (_error, variables, { context, invalidates } = {}) => ({
  variables,
  invalidates,
  ...context,
});

export function createQueryClient({
  defaultOptions = defaultQueryClientOptions,
  queryErrorContext = defaultQueryErrorContext,
  shouldLogQueryError = defaultShouldLogQueryError,
  mutationErrorContext = defaultMutationErrorContext,
}: CreateQueryClientOptions = {}) {
  const queryClient = new QueryClient({
    defaultOptions,

    queryCache: new QueryCache({
      onError: (error, { queryKey, meta: queryMeta }) => {
        if (!shouldLogQueryError(error, queryKey, queryMeta)) return;

        logError({ error, context: queryErrorContext(error, queryKey, queryMeta) });
      },
    }),

    mutationCache: new MutationCache({
      onError: (error, variables, _context, { meta: mutationMeta }) => {
        logError({ error, context: mutationErrorContext(error, variables, mutationMeta) });
      },

      onSuccess: (_data, _variables, _context, { meta: mutationMeta }) => {
        queryClient.invalidateQueries({
          predicate: ({ meta: queryMeta }) =>
            mutationMeta?.invalidates?.some((tag) => queryMeta?.tags?.includes(tag)) ?? false,
        });
      },
    }),
  });

  return queryClient;
}
